/**
 * Emberreach client entry point.
 *
 * Wiring only: screens, input, networking, the render loop and zone changes.
 * The game rules live in @emberreach/shared (shared with the authoritative
 * server), the geometry in renderer.ts, the world in shared/game/world.
 * Nothing here knows the name of a town.
 */

import { Color3 } from '@babylonjs/core/Maths/math.color.js';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder.js';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial.js';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode.js';
import { SceneInstrumentation } from '@babylonjs/core/Instrumentation/sceneInstrumentation.js';
import type { Scene } from '@babylonjs/core/scene.js';
import type { AbstractEngine } from '@babylonjs/core/Engines/abstractEngine.js';
import type { UniversalCamera } from '@babylonjs/core/Cameras/universalCamera.js';
import type { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator.js';

import type { CharacterSummary, PlayerState, ZoneDef } from '@emberreach/shared';
import {
  DEFAULT_ZONE_ID, buildingFootprints, exitUnderPlayer, getZone,
  groundHeightAt, step, validateDisplayName, validateEmail, validatePassword,
  validateZones, worldToIntent
} from '@emberreach/shared';

import { api, ApiError, accessTokenForSocket, isSignedIn } from './api/client.js';
import { chooseCharacter } from './ui/characterScreens.js';
import { ZoneConnection } from './net/room.js';
import { RemoteRenderer } from './game/remotes.js';
import { ActorRenderer } from './game/actors.js';
import { GamePanels, toast } from './ui/panels.js';
import { ChatBox } from './ui/chatBox.js';
import {
  buildAdSlot, buildBuilding, buildProp, buildTerrain, createEngine, createScene
} from './game/renderer.js';
import {
  createCameraState, applyLook, applyZoom, updateCamera, type CameraState
} from './game/camera.js';
import { consumeFrame, createInputState, detectTouchDevice, type InputSource } from './game/input/index.js';
import { createKeyboardMouse } from './game/input/keyboard.js';
import { createTouchControls } from './game/input/touch.js';
import {
  FpsMeter, QUALITY, adjustQuality, chooseInitialQuality, readDeviceHints,
  type QualityLevel, type QualitySettings
} from './game/quality.js';
import {
  applyAccessibility, loadSettings, saveSettings, sensitivityToRadians, type PlayerSettings
} from './ui/settings.js';
import { AdBoards } from './game/adBoards.js';
import { buildFigure, poseWalk, type FigureRig } from './game/figure.js';
import { SponsorPanel } from './ui/sponsorPanel.js';
import { DeathNotice, HelpScreen } from './ui/help.js';
import { DEFAULT_VISIBILITY_RULES } from '@emberreach/shared';
import type { Mesh } from '@babylonjs/core/Meshes/mesh.js';

// --- small DOM helpers -----------------------------------------------------

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (node === null) throw new Error(`missing element #${id}`);
  return node as T;
}
const show = (node: HTMLElement): void => node.classList.remove('hidden');
const hide = (node: HTMLElement): void => node.classList.add('hidden');

const dom = {
  canvas: el<HTMLCanvasElement>('game-canvas'),
  loading: el('loading'),
  loadingBar: el('loading-bar'),
  loadingZone: el('loading-zone'),
  loadingDetail: el('loading-detail'),
  auth: el('auth'),
  hud: el('hud'),
  settings: el('settings'),
  touchUi: el('touch-ui'),
  zoneName: el('zone-name'),
  diagnostics: el('diagnostics'),
  interact: el('interact-prompt'),
  connection: el('connection-banner'),
  nameplates: el('nameplates'),
  playerLabel: el('player-label')
};

function progress(percent: number, detail = ''): void {
  dom.loadingBar.style.width = `${Math.max(0, Math.min(100, percent))}%`;
  dom.loadingDetail.textContent = detail;
}

function banner(message: string | null): void {
  if (message === null) { hide(dom.connection); return; }
  dom.connection.textContent = message;
  show(dom.connection);
}

// --- state -----------------------------------------------------------------

let settings: PlayerSettings = loadSettings();
let quality: QualitySettings = QUALITY.medium;
let qualityLevel: QualityLevel = 'medium';
let secondsAtQuality = 0;

let engine: AbstractEngine | null = null;
let instrumentation: SceneInstrumentation | null = null;
let scene: Scene | null = null;
let camera: UniversalCamera | null = null;
let shadows: ShadowGenerator | null = null;
let usingWebGpu = false;

let zone: ZoneDef = getZone(DEFAULT_ZONE_ID)!;
let footprints = buildingFootprints(zone);
let zoneRoot: TransformNode | null = null;
let playerMesh: TransformNode | null = null;
/** The player's limbs, so the walk cycle has something to move. */
let playerRig: FigureRig | null = null;
/** Last frame's position, for working out how fast the legs should move. */
const lastPosition = { x: 0, z: 0 };

let player: PlayerState = { x: 0, y: 0, z: 0, facing: 0, velocityY: 0, grounded: true };
let cameraState: CameraState = createCameraState(0);
let character: CharacterSummary | null = null;

const input = createInputState();
let keyboardSource: InputSource | null = null;
let touchSource: InputSource | null = null;
const isTouch = detectTouchDevice();

let connection: ZoneConnection | null = null;
let remoteRenderer: RemoteRenderer | null = null;
let actorRenderer: ActorRenderer | null = null;
let panels: GamePanels | null = null;
let chatBox: ChatBox | null = null;

/* Advertising. The slot meshes are rebuilt with the zone, so the map is
   rebuilt with it too — holding stale meshes here would draw a campaign onto
   a billboard that no longer exists. */
let adBoards: AdBoards | null = null;
let sponsorPanel: SponsorPanel | null = null;
let helpScreen: HelpScreen | null = null;
let deathNotice: DeathNotice | null = null;

/**
 * Where a click told the player to walk, in world coordinates.
 *
 * Cleared the moment a movement key is touched: somebody who has grabbed the
 * keyboard has changed their mind, and a walk order still pulling them the
 * other way is exactly what makes click-to-move feel broken.
 */
let walkTo: { x: number; z: number } | null = null;
/** Set by clicking a creature; cleared when it dies or leaves the world. */
let clickTarget: string | null = null;
let adSlotMeshes = new Map<string, Mesh>();
/** Views are batched rather than sent one at a time. */
let secondsSinceViewFlush = 0;
const VIEW_FLUSH_SECONDS = 15;
/** Local copy of vitals, so the HUD does not wait on a round trip. */
let vitals = { health: 100, maxHealth: 100, level: 1 };

function renderVitals(): void {
  const fraction = vitals.maxHealth > 0 ? vitals.health / vitals.maxHealth : 0;
  el('bar-health').style.width = `${Math.max(0, Math.min(100, fraction * 100))}%`;
  if (character !== null) {
    dom.playerLabel.textContent =
      `${character.name} · level ${vitals.level} ${character.className}`;
  }
}

const fps = new FpsMeter(90);
let zoneChanging = false;

// --- world building --------------------------------------------------------

function disposeZone(): void {
  zoneRoot?.dispose(false, true);
  zoneRoot = null;
}

function buildZone(target: ZoneDef): void {
  if (scene === null) return;
  disposeZone();

  const root = new TransformNode(`zone-${target.id}`, scene);
  zoneRoot = root;

  for (const mesh of buildTerrain(scene, target, quality)) mesh.parent = root;

  for (const building of target.buildings) {
    const ground = groundHeightAt(building.position[0], building.position[1], target);
    buildBuilding(scene, building, ground, quality, shadows).parent = root;
  }

  for (const prop of target.props) {
    const ground = groundHeightAt(prop.position[0], prop.position[1], target);
    buildProp(scene, prop, ground, quality, shadows).parent = root;
  }

  // Any campaign hung on the old zone's meshes goes with them.
  adBoards?.clear();
  adSlotMeshes = new Map<string, Mesh>();
  for (const slot of target.adSlots) {
    const visible = isTouch ? slot.showOnMobile : slot.showOnDesktop;
    if (!visible) continue;
    const built = buildAdSlot(scene, slot, quality);
    built.root.parent = root;
    adSlotMeshes.set(slot.id, built.panel);
  }

  for (const exit of target.exits) {
    const ground = groundHeightAt(exit.position[0], exit.position[1], target);
    const ring = MeshBuilder.CreateTorus(
      `exit-${exit.id}`,
      { diameter: exit.radius * 2, thickness: 0.22, tessellation: 24 },
      scene
    );
    ring.position.set(exit.position[0], ground + 0.12, exit.position[1]);
    const mat = new StandardMaterial(`exit-mat-${exit.id}`, scene);
    mat.emissiveColor = Color3.FromHexString('#ffb14a');
    mat.diffuseColor = Color3.FromHexString('#3a2410');
    ring.material = mat;
    ring.isPickable = false;
    ring.parent = root;
  }

  dom.zoneName.textContent = target.name;
}

function createPlayerMesh(): void {
  if (scene === null) return;
  playerMesh?.dispose(false, true);

  /* A person, built from the appearance the player actually chose. The old
     version was a capsule with a sphere on it and tinted only the skin tone —
     which quietly threw away the body type, the hairstyle and the outfit the
     character creator had just spent a screen asking about. */
  playerRig = buildFigure(scene, character?.appearance, {
    shadows,
    name: 'player'
  });
  playerMesh = playerRig.root;
}

function enterZone(target: ZoneDef, at?: { x: number; y: number; z: number; facing: number }): void {
  /* Before `zone` changes, not after. Views are reported with the zone they
     were seen in, and the batch still pending here belongs to the town being
     left, not the one being walked into. */
  flushAdViews();
  secondsSinceViewFlush = 0;

  zone = target;
  footprints = buildingFootprints(target);
  buildZone(target);

  const x = at?.x ?? target.spawn[0];
  const z = at?.z ?? target.spawn[2];
  player = {
    x,
    y: at?.y ?? groundHeightAt(x, z, target),
    z,
    facing: at?.facing ?? target.spawnFacing,
    velocityY: 0,
    grounded: true
  };
  cameraState = createCameraState(player.facing);

  if (scene !== null) {
    scene.fogColor = Color3.FromHexString(target.ambience.fogColour);
    scene.fogDensity = target.ambience.fogDensity;
  }

  void loadZoneAds(target.id);
}

// --- advertising -----------------------------------------------------------

/**
 * Asks what is booked in this zone and hangs it on the slots.
 *
 * A failure here is silent on purpose: an advertisement that will not load is
 * not a reason to interrupt somebody's game. The slot simply stays blank.
 */
async function loadZoneAds(zoneId: string): Promise<void> {
  if (character === null || adBoards === null) return;
  const requested = zoneId;

  try {
    const { ads, rules } = await api.zoneAds(character.id, zoneId);
    // A slow reply can land after the player has already walked out of the
    // zone. Hanging it on the new zone's slots would show the wrong town's
    // advertisements, and bill for them.
    if (zone.id !== requested || adBoards === null) return;
    adBoards.setRules(rules);
    adBoards.apply(ads, adSlotMeshes);
  } catch (err) {
    /* Silent for the player — the slot just stays blank — but not silent for
       anyone debugging. An earlier version swallowed this completely, and a
       failure inside apply() was indistinguishable from a campaign nobody had
       booked. */
    console.warn('advertising did not load for this zone', err);
  }
}

/** Sends whatever views have accrued. Safe to call when there are none. */
function flushAdViews(): void {
  adBoards?.flush();
}

// --- test hooks ------------------------------------------------------------

/**
 * Handles the headless advertising test drives the game through.
 *
 * Compiled out of a normal build: `import.meta.env.VITE_TEST_HOOKS` is
 * substituted at build time, so without it the whole branch is statically
 * false and neither of these exists in what ships.
 *
 * Even with the flag on, nothing here is a cheat. The server is authoritative
 * over position and corrects anything it disagrees with; standing the player
 * in front of a billboard only saves the test from driving the keyboard across
 * a town for thirty seconds, and any view it then reports is judged by the
 * same rules as everybody else's.
 */
function installTestHooks(): void {
  const handle = dom.canvas as HTMLCanvasElement & Record<string, unknown>;
  handle['__emberreachScene'] = scene;

  handle['__emberreachTeleport'] = (
    target: { x: number; y: number; z: number }, side = 1
  ): { x: number; z: number } => {
    /* Stands back from the board and faces it. Which side is the front depends
       on how the slot was rotated, so the caller passes `side` and can try the
       other one — cheaper and more honest than guessing at plane normals. */
    const from = { x: target.x, z: target.z + 6 * side };
    const dx = target.x - from.x;
    const dz = target.z - from.z;
    const facing = Math.atan2(dx, dz);

    player = {
      x: from.x,
      y: groundHeightAt(from.x, from.z, zone),
      z: from.z,
      facing,
      velocityY: 0,
      grounded: true
    };
    cameraState = createCameraState(facing);
    return { x: from.x, z: from.z };
  };
}

function applyQuality(level: QualityLevel): void {
  qualityLevel = level;
  quality = QUALITY[level];
  secondsAtQuality = 0;
  engine?.setHardwareScalingLevel(1 / quality.renderScale);
  buildZone(zone);

  /* The zone was just rebuilt, which means brand new blank ad panels. Without
     this, a quality change — including the automatic one that fires when the
     frame rate dips — silently strips every booked advertisement out of the
     world until the player walks into another town. Advertisers would be
     paying for boards nobody could see. */
  void loadZoneAds(zone.id);
}

// --- networking ------------------------------------------------------------

async function connectToZone(zoneId: string): Promise<void> {
  if (character === null) return;

  await connection?.leave().catch(() => undefined);

  connection = new ZoneConnection({
    onCorrection(correction) {
      /* The server disagreed about where we are, so it wins.
         Velocity is reset too — keeping it would carry the momentum that caused
         the disagreement straight back into the next tick. */
      player = { ...player, ...correction, velocityY: 0, grounded: true };
    },
    onZoneChanged(nextZoneId) {
      const next = getZone(nextZoneId);
      if (next === undefined) return;
      void travelTo(next);
    },
    onDisconnected(reason) {
      banner(reason === 'dropped'
        ? 'Connection lost. Other players are hidden until it comes back.'
        : null);
      remoteRenderer?.dispose();
    },
    onReconnecting() { banner('Reconnecting…'); },
    onReconnected() { banner(null); },

    onCombat(message) {
      if (message.kind === 'playerHit' && message.sessionId === undefined) return;
      // Only our own damage moves the HUD; other people's is drawn by their bars.
      if (message.kind === 'playerHit' && message.damage !== undefined) {
        const mine = connection?.remotes.has(message.sessionId ?? '') === false;
        if (mine) {
          vitals.health = Math.max(0, vitals.health - message.damage);
          renderVitals();
          dom.canvas.classList.add('hurt');
          window.setTimeout(() => dom.canvas.classList.remove('hurt'), 160);
        }
      }
      if (message.kind === 'playerDied') {
        const mine = connection?.remotes.has(message.sessionId ?? '') === false;
        if (mine) {
          vitals.health = 0;
          renderVitals();
          // A walk order that outlives your death would march you straight back
          // into whatever killed you the moment you stood up.
          walkTo = null;
          clickTarget = null;
          deathNotice?.show({
            killedBy: message.killedBy ?? 'something',
            enemyLevel: message.enemyLevel ?? null,
            yourLevel: vitals.level,
            placeName: zone.name,
            seconds: message.respawnSeconds ?? 5
          });
        }
      }
    },

    onKill(report) {
      const loot = report.loot.map((l) => `${l.itemKey} ×${l.quantity}`).join(', ');
      toast(
        `${report.name} down · +${report.experience} xp` +
        (report.marks > 0 ? ` · +${report.marks} Marks` : '') +
        (loot !== '' ? ` · ${loot}` : ''),
        'good'
      );
      if (report.levelledUp) {
        vitals.level = report.level;
        toast(`Level ${report.level}!`, 'good');
      }
      renderVitals();
      void panels?.refreshQuests();
    },

    onRespawn(at) {
      player = { x: at.x, y: at.y, z: at.z, facing: at.facing, velocityY: 0, grounded: true };
      cameraState = createCameraState(at.facing);
      vitals.health = at.health;
      renderVitals();
      deathNotice?.hide();
      toast('Back on your feet.', 'good');
    },

    onChat(message) { chatBox?.add(message); },
    onChatHistory(messages) { chatBox?.history(messages); },
    onChatRejected(reason) { chatBox?.system(reason); }
  });

  connection.reportPosition = () => ({ x: player.x, z: player.z });

  const token = await accessTokenForSocket();
  await connection.join(zoneId, token, character.id);
  banner(null);

  if (scene !== null) {
    remoteRenderer?.dispose();
    remoteRenderer = new RemoteRenderer(scene, dom.nameplates);

    actorRenderer?.dispose();
    actorRenderer = new ActorRenderer(scene, dom.nameplates, shadows);
    try {
      const { npcs } = await api.npcs(zoneId);
      actorRenderer.buildNpcs(npcs, (x, z) => groundHeightAt(x, z, zone));
    } catch {
      // A zone without its NPCs is still walkable; say nothing and carry on.
    }
  }
}

async function travelTo(next: ZoneDef): Promise<void> {
  if (zoneChanging) return;
  zoneChanging = true;
  try {
    dom.loadingZone.textContent = next.blurb;
    show(dom.loading);
    progress(30, `Travelling to ${next.name}…`);

    remoteRenderer?.dispose();
    enterZone(next);
    await connectToZone(next.id);

    progress(100, '');
    hide(dom.loading);
  } catch (err) {
    hide(dom.loading);
    banner('Could not travel there. Try again in a moment.');
    console.error(err);
  } finally {
    zoneChanging = false;
  }
}

// --- the loop --------------------------------------------------------------

function updateDiagnostics(): void {
  if (!settings.showDiagnostics || engine === null || scene === null) return;
  const drawCalls = instrumentation?.drawCallsCounter.current ?? 0;
  dom.diagnostics.textContent =
    `fps        ${fps.fps.toFixed(0)}\n` +
    `draw calls ${drawCalls}\n` +
    `meshes     ${scene.getActiveMeshes().length} / ${scene.meshes.length}\n` +
    `quality    ${qualityLevel}\n` +
    `renderer   ${usingWebGpu ? 'WebGPU' : 'WebGL2'}\n` +
    `players    ${connection?.playerCount ?? 1}\n` +
    `position   ${player.x.toFixed(1)}, ${player.z.toFixed(1)}`;
}

/** Close enough to a walk order to call it arrived. */
const ARRIVE_METRES = 0.6;
/** How close a click-targeted creature has to be before swinging. */
const STRIKE_METRES = 2.2;

/**
 * Turns a click on the canvas into an intention.
 *
 * One ray, and what it hits decides the verb. Creatures and townsfolk carry an
 * id in the metadata of an invisible box around them (see actors.ts); the
 * ground and the roads are flagged `walkable`. Anything else — a wall, a roof,
 * a cart — is ignored on purpose, because "walk into that building" is never
 * what somebody meant.
 */
function resolveClick(x: number, y: number): void {
  if (scene === null) return;

  const pick = scene.pick(x, y, (mesh) => mesh.isPickable && mesh.isEnabled());
  if (pick === null || !pick.hit || pick.pickedMesh === null) return;

  const meta = pick.pickedMesh.metadata as
    { enemyId?: string; npcKey?: string; walkable?: boolean } | null;

  if (meta?.enemyId !== undefined) {
    clickTarget = meta.enemyId;
    const enemy = connection?.enemies.get(meta.enemyId);
    // Walk to it as well as marking it: a click on something across the square
    // should close the distance rather than swing at thin air.
    if (enemy !== undefined) walkTo = { x: enemy.x, z: enemy.z };
    return;
  }

  if (meta?.npcKey !== undefined) {
    const target = actorRenderer?.npcByKey(meta.npcKey) ?? null;
    clickTarget = null;
    if (target === null) return;

    // Close enough already? Then talking is what was meant. Otherwise walk over
    // and let the interact prompt take it from there — walking somebody into a
    // conversation they did not ask for is worse than one extra press of E.
    if (Math.hypot(target.x - player.x, target.z - player.z) < 4) {
      walkTo = null;
      void panels?.talkTo(meta.npcKey);
    } else {
      walkTo = { x: target.x, z: target.z };
    }
    return;
  }

  if (meta?.walkable === true && pick.pickedPoint !== null) {
    walkTo = { x: pick.pickedPoint.x, z: pick.pickedPoint.z };
    clickTarget = null;
  }
}

/**
 * Produces the movement intent that carries out a walk order.
 *
 * Returns null when there is nothing to carry out, and the keyboard's own
 * intent is used instead. Everything goes through the same camera-space intent
 * the keys produce — `worldToIntent` is the whole reason this is five lines and
 * not a second movement system for the server to disagree with.
 */
function steerTowardsTarget(): { x: number; z: number } | null {
  // A targeted creature moves, so re-aim at it every frame rather than at the
  // patch of ground it was standing on when it was clicked.
  if (clickTarget !== null) {
    const enemy = connection?.enemies.get(clickTarget) ?? null;
    if (enemy === null || enemy.mode === 'dead') {
      clickTarget = null;
      walkTo = null;
    } else {
      const gap = Math.hypot(enemy.x - player.x, enemy.z - player.z);
      if (gap <= STRIKE_METRES) {
        walkTo = null;
        connection?.attack();
        return { x: 0, z: 0 };
      }
      walkTo = { x: enemy.x, z: enemy.z };
    }
  }

  if (walkTo === null) return null;

  const dx = walkTo.x - player.x;
  const dz = walkTo.z - player.z;
  const distance = Math.hypot(dx, dz);
  if (distance <= ARRIVE_METRES) { walkTo = null; return { x: 0, z: 0 }; }

  const [ix, iz] = worldToIntent(dx / distance, dz / distance, cameraState.yaw);
  return { x: ix, z: iz };
}

function tick(): void {
  if (scene === null || engine === null || camera === null) return;

  const dt = engine.getDeltaTime() / 1000;
  fps.record(dt);
  secondsAtQuality += dt;

  const frame = consumeFrame(input);

  /* Movement is suppressed while a panel is open or the chat box has focus.
     Without this, typing "was" in chat walks the character into a wall, and
     browsing a shop slides you out of range of the shopkeeper. */
  const uiHasFocus = panels?.anyOpen === true || chatBox?.focused === true
    || sponsorPanel?.isOpen === true || helpScreen?.isOpen === true
    || deathNotice?.isOpen === true;

  if (!uiHasFocus) {
    for (const click of frame.worldClicks) resolveClick(click.x, click.y);
    if (frame.zoomDelta !== 0) applyZoom(cameraState, frame.zoomDelta);
  }

  /* A key beats a click. Holding W while a walk order is running would
     otherwise fight it, and the player would feel the character resisting. */
  const keyMoving = frame.moveX !== 0 || frame.moveZ !== 0;
  if (keyMoving) { walkTo = null; clickTarget = null; }

  const steered = uiHasFocus ? null : steerTowardsTarget();

  const move = uiHasFocus
    ? { x: 0, z: 0, running: false, jump: false }
    : {
      x: steered?.x ?? frame.moveX,
      z: steered?.z ?? frame.moveZ,
      running: frame.running,
      jump: frame.jump
    };

  if (!uiHasFocus) applyLook(cameraState, frame.lookYaw, frame.lookPitch);

  // Predicted locally so movement is instant, then corrected by the server.
  player = step(player, move, cameraState.yaw, zone, footprints, dt);

  connection?.sendIntent({
    x: move.x, z: move.z,
    running: move.running, jump: move.jump,
    cameraYaw: cameraState.yaw
  });

  if (playerMesh !== null) {
    /* Speed measured from where the character actually ended up, not from the
       input. A player walking into a wall is holding forward and going
       nowhere — legs pumping on the spot is the classic tell. */
    const travelled = Math.hypot(player.x - lastPosition.x, player.z - lastPosition.z);
    lastPosition.x = player.x;
    lastPosition.z = player.z;

    playerMesh.position.set(player.x, player.y, player.z);
    playerMesh.rotation.y = player.facing;
    if (playerRig !== null) poseWalk(playerRig, dt > 0 ? travelled / dt : 0, dt);
  }

  connection?.interpolate(dt);
  if (remoteRenderer !== null && connection !== null) {
    remoteRenderer.update(connection.remotes, camera, dom.canvas, dt);
  }

  updateCamera(camera, cameraState, player, scene, dt);

  /* Measured after the camera has moved, so the numbers describe the frame the
     player is actually about to be shown. Doing it before would credit an
     advertiser for a board the camera has already turned away from. */
  if (adBoards !== null) {
    adBoards.update(camera, dt * 1000, !document.hidden);
    secondsSinceViewFlush += dt;
    if (secondsSinceViewFlush >= VIEW_FLUSH_SECONDS) {
      secondsSinceViewFlush = 0;
      flushAdViews();
    }
  }

  // Enemies are drawn where the server says they are, not eased — the question
  // "am I in reach to hit it?" has to agree with the server's answer.
  if (actorRenderer !== null && connection !== null) {
    actorRenderer.updateEnemies(connection.enemies, dt);
    actorRenderer.updateLabels(camera, dom.canvas, connection.enemies);
  }

  /* One interact key, and the game decides what it means from where you are
     standing: an exit, an enemy in reach, or somebody to talk to. A separate
     button per verb is a lot of buttons for a thumb. */
  const exit = exitUnderPlayer(player, zone);
  const npc = uiHasFocus ? null : actorRenderer?.nearestNpc(player.x, player.z) ?? null;
  const foe = uiHasFocus || connection === null
    ? null
    : actorRenderer?.nearestEnemy(connection.enemies, player.x, player.z) ?? null;
  const sign = uiHasFocus ? null : adBoards?.focused() ?? null;

  const verb = isTouch ? 'Tap ✋' : 'Press E';
  if (exit !== null) {
    dom.interact.textContent = `${verb} to travel to ${exit.label}`;
    show(dom.interact);
    if (frame.actions.includes('interact') && !zoneChanging) {
      // Asks the server, which checks we are really standing in the exit.
      // Without that check "change zone" would be a free teleport.
      connection?.requestZoneChange(exit.toZone);
    }
  } else if (npc !== null) {
    dom.interact.textContent = `${verb} to talk to ${npc.name}`;
    show(dom.interact);
    if (frame.actions.includes('interact')) void panels?.talkTo(npc.key);
  } else if (foe !== null) {
    dom.interact.textContent = isTouch ? `${foe.name} — tap ⚔` : `${foe.name} — click to attack`;
    show(dom.interact);
  } else if (sign !== null) {
    // Last in the chain on purpose. Reading an advertisement must never take
    // precedence over an exit, a shopkeeper or something attacking you.
    dom.interact.textContent = `${verb} to read the sign`;
    show(dom.interact);
    if (frame.actions.includes('interact')) sponsorPanel?.open(sign);
  } else {
    hide(dom.interact);
  }

  if (frame.actions.includes('attack') && !uiHasFocus) connection?.attack();
  // Help is deliberately outside the uiHasFocus guard — it is the one thing a
  // player might want while a panel they do not understand is open.
  if (frame.actions.includes('help')) helpScreen?.toggle();
  if (frame.actions.includes('settings') && panels?.anyOpen !== true
    && helpScreen?.isOpen !== true) openSettings();
  if (frame.actions.includes('inventory')) void panels?.openBag();
  if (frame.actions.includes('map')) void panels?.openQuests();
  if (frame.actions.includes('chat')) chatBox?.focus();

  if (settings.quality === 'auto') {
    const next = adjustQuality(qualityLevel, fps.fps, secondsAtQuality);
    if (next !== qualityLevel) applyQuality(next);
  }

  updateDiagnostics();
  scene.render();
}

// --- start-up --------------------------------------------------------------

function buildTouchControls(sensitivity: number): InputSource {
  return createTouchControls(
    input,
    {
      stickZone: el('stick-zone'),
      stickKnob: el('stick-knob'),
      lookZone: el('look-zone'),
      canvas: dom.canvas,
      buttons: [
        { element: el('btn-attack'), action: 'attack' },
        { element: el('btn-interact'), action: 'interact' },
        { element: el('btn-jump'), action: 'jump' },
        { element: el('btn-ability1'), action: 'ability1' },
        { element: el('btn-ability2'), action: 'ability2' },
        { element: el('btn-ability3'), action: 'ability3' },
        { element: el('btn-ability4'), action: 'ability4' },
        { element: el('btn-inventory'), action: 'inventory' },
        { element: el('btn-map'), action: 'map' },
        { element: el('btn-settings'), action: 'settings' }
      ]
    },
    { sensitivity: sensitivity * 1.5, invertY: settings.invertY }
  );
}

async function startGame(chosen: CharacterSummary): Promise<void> {
  character = chosen;
  show(dom.loading);
  progress(20, 'Starting the renderer…');

  const hints = readDeviceHints();
  qualityLevel = settings.quality === 'auto' ? chooseInitialQuality(hints) : settings.quality;
  quality = QUALITY[qualityLevel];

  const created = await createEngine(dom.canvas, quality);
  engine = created.engine;
  usingWebGpu = created.webgpu;
  engine.setHardwareScalingLevel(1 / quality.renderScale);

  progress(45, 'Building the world…');
  const startZone = getZone(chosen.zoneId) ?? getZone(DEFAULT_ZONE_ID)!;
  const built = createScene(engine, startZone, quality);
  scene = built.scene;
  camera = built.camera;
  shadows = built.shadows;

  createPlayerMesh();

  /* Built before the first enterZone, because enterZone is what asks for the
     zone's advertisements. The real rules arrive with that reply; these are
     the defaults so the first frames measure by something sensible. */
  adBoards = new AdBoards(scene, DEFAULT_VISIBILITY_RULES);
  adBoards.setDevice(isTouch ? 'mobile' : 'desktop');
  adBoards.onViews = (views) => {
    if (character === null) return;
    // Nothing is retried. A lost batch costs an advertiser a few impressions;
    // a retry queue risks charging for the same look twice.
    void api.reportViews(character.id, zone.id, views).catch(() => undefined);
  };

  sponsorPanel = new SponsorPanel(document.body, {
    onOpen: (ad) => {
      if (character === null) return;
      void api.openedPanel(character.id, ad.campaignId, ad.slotId, zone.id)
        .catch(() => undefined);
    },
    onKeep: async (ad) => {
      if (character === null) return false;
      try {
        await api.keepOffer(character.id, ad.campaignId, ad.slotId, zone.id);
        return true;
      } catch {
        return false;
      }
    }
  });

  helpScreen = new HelpScreen(document.body, isTouch);
  deathNotice = new DeathNotice(document.body);
  el('btn-help').addEventListener('click', () => helpScreen?.toggle());

  enterZone(startZone);

  progress(65, 'Setting up controls…');
  const sensitivity = sensitivityToRadians(settings.sensitivity);
  keyboardSource = createKeyboardMouse(input, dom.canvas, { sensitivity, invertY: settings.invertY });
  keyboardSource.attach();

  if (isTouch) {
    show(dom.touchUi);
    document.body.classList.add('touch-controls');
    touchSource = buildTouchControls(sensitivity);
    touchSource.attach();
    hide(el('btn-settings-desktop'));
  }

  /* First time in, open the help by itself. Nobody reads a "press H" hint they
     have not been given a reason to look for, and the alternative is what
     happened to the first player: killed by something whose level they had not
     noticed, back at the gate, no idea why. */
  if (HelpScreen.neverSeen()) helpScreen.open();

  progress(85, 'Joining the world…');
  try {
    await connectToZone(startZone.id);
  } catch (err) {
    // A failed connection must not stop the game loading. The world is still
    // walkable; the player simply cannot see anyone else yet.
    console.error(err);
    banner('Playing offline — could not reach the game server.');
  }

  vitals = { health: chosen.health, maxHealth: chosen.maxHealth, level: chosen.level };
  renderVitals();

  panels = new GamePanels(chosen.id);
  panels.onVitals = (v) => {
    if (v.health !== undefined) vitals.health = v.health;
    if (v.maxHealth !== undefined) vitals.maxHealth = v.maxHealth;
    if (v.level !== undefined) vitals.level = v.level;
    renderVitals();
  };
  void panels.refreshQuests();

  chatBox = new ChatBox((body) => connection?.say(body));
  chatBox.onReport = (messageId) => {
    connection?.report(messageId);
    toast('Reported. Thank you.', 'good');
  };

  dom.diagnostics.classList.toggle('hidden', !settings.showDiagnostics);
  if (settings.showDiagnostics && scene !== null) {
    instrumentation = new SceneInstrumentation(scene);
    instrumentation.captureFrameTime = false;
  }

  window.addEventListener('resize', () => engine?.resize());

  document.addEventListener('visibilitychange', () => {
    if (engine === null) return;
    if (document.hidden) {
      // Send what has been seen before the loop stops, or a tab left open in
      // the background loses the last batch entirely.
      flushAdViews();
      engine.stopRenderLoop();
    } else {
      engine.runRenderLoop(tick);
    }
  });

  // Leaving cleanly saves the position server-side instead of waiting for the
  // reconnection window to lapse.
  window.addEventListener('pagehide', () => {
    flushAdViews();
    void connection?.leave();
  });

  /* Dot access, not import.meta.env['VITE_TEST_HOOKS']. Vite only substitutes
     the static dot form at build time; the bracket form survives into the
     bundle as a lookup that is always undefined, so the hooks would never
     install and, worse, the branch would never be dropped from production. */
  if (import.meta.env.VITE_TEST_HOOKS === '1') installTestHooks();

  progress(100, '');
  hide(dom.loading);
  show(dom.hud);
  dom.hud.classList.add('playing');

  engine.runRenderLoop(tick);
}

// --- settings screen -------------------------------------------------------

function openSettings(): void {
  const quality$ = el<HTMLSelectElement>('set-quality');
  const sens$ = el<HTMLInputElement>('set-sensitivity');

  quality$.value = settings.quality;
  sens$.value = String(settings.sensitivity);
  el<HTMLInputElement>('set-invert').checked = settings.invertY;
  el<HTMLInputElement>('set-diagnostics').checked = settings.showDiagnostics;
  el<HTMLInputElement>('set-reduced-motion').checked = settings.reducedMotion;
  el<HTMLInputElement>('set-text-scale').value = String(settings.textScale);
  el('sens-value').textContent = String(settings.sensitivity);
  el('text-scale-value').textContent = `${settings.textScale}%`;
  el('renderer-info').textContent =
    `Renderer: ${usingWebGpu ? 'WebGPU' : 'WebGL2'} · quality ${qualityLevel} · ` +
    `${isTouch ? 'touch' : 'keyboard and mouse'} · ` +
    `${connection?.connected === true ? 'connected' : 'offline'}`;

  show(dom.settings);
}

function restartInput(): void {
  const sensitivity = sensitivityToRadians(settings.sensitivity);
  keyboardSource?.detach();
  keyboardSource = createKeyboardMouse(input, dom.canvas, { sensitivity, invertY: settings.invertY });
  keyboardSource.attach();

  if (touchSource !== null && isTouch) {
    touchSource.detach();
    touchSource = buildTouchControls(sensitivity);
    touchSource.attach();
  }
}

function wireSettings(): void {
  const persist = (): void => { saveSettings(settings); applyAccessibility(settings); };

  el<HTMLSelectElement>('set-quality').addEventListener('change', (e) => {
    const value = (e.target as HTMLSelectElement).value;
    settings.quality = value === 'low' || value === 'medium' || value === 'high' ? value : 'auto';
    persist();
    applyQuality(settings.quality === 'auto' ? chooseInitialQuality(readDeviceHints()) : settings.quality);
  });

  el<HTMLInputElement>('set-sensitivity').addEventListener('input', (e) => {
    settings.sensitivity = Number((e.target as HTMLInputElement).value);
    el('sens-value').textContent = String(settings.sensitivity);
    persist();
    restartInput();
  });

  el<HTMLInputElement>('set-invert').addEventListener('change', (e) => {
    settings.invertY = (e.target as HTMLInputElement).checked;
    persist();
    restartInput();
  });

  el<HTMLInputElement>('set-diagnostics').addEventListener('change', (e) => {
    settings.showDiagnostics = (e.target as HTMLInputElement).checked;
    dom.diagnostics.classList.toggle('hidden', !settings.showDiagnostics);
    if (settings.showDiagnostics && scene !== null && instrumentation === null) {
      instrumentation = new SceneInstrumentation(scene);
      instrumentation.captureFrameTime = false;
    }
    persist();
  });

  el<HTMLInputElement>('set-reduced-motion').addEventListener('change', (e) => {
    settings.reducedMotion = (e.target as HTMLInputElement).checked;
    persist();
  });

  el<HTMLInputElement>('set-text-scale').addEventListener('input', (e) => {
    settings.textScale = Number((e.target as HTMLInputElement).value);
    el('text-scale-value').textContent = `${settings.textScale}%`;
    persist();
  });

  el('settings-close').addEventListener('click', () => hide(dom.settings));
  el('btn-settings-desktop').addEventListener('click', openSettings);
  el('settings-signout').addEventListener('click', () => { void signOut(); });
}

async function signOut(): Promise<void> {
  await connection?.leave().catch(() => undefined);
  await api.logout().catch(() => undefined);
  window.location.reload();
}

// --- authentication screen -------------------------------------------------

function setFieldError(id: string, message: string | null): void {
  el(id).textContent = message ?? '';
}

/** Character select, then into the world. Shared by sign-in and session restore. */
async function afterSignIn(): Promise<void> {
  hide(dom.auth);
  hide(dom.loading);
  const chosen = await chooseCharacter(() => { void signOut(); });
  await startGame(chosen);
}

function wireAuth(): void {
  const loginTab = el<HTMLButtonElement>('tab-login');
  const registerTab = el<HTMLButtonElement>('tab-register');
  const loginForm = el<HTMLFormElement>('form-login');
  const registerForm = el<HTMLFormElement>('form-register');

  loginTab.addEventListener('click', () => {
    loginTab.classList.add('active'); registerTab.classList.remove('active');
    show(loginForm); hide(registerForm);
  });
  registerTab.addEventListener('click', () => {
    registerTab.classList.add('active'); loginTab.classList.remove('active');
    show(registerForm); hide(loginForm);
  });

  loginForm.addEventListener('submit', (e) => {
    e.preventDefault();
    const email = el<HTMLInputElement>('login-email').value;
    const password = el<HTMLInputElement>('login-password').value;
    const notice = el('login-notice');
    notice.textContent = '';
    notice.classList.remove('bad');
    setFieldError('login-email-error', null);
    setFieldError('login-password-error', null);

    const button = loginForm.querySelector<HTMLButtonElement>('button.primary');
    if (button !== null) button.disabled = true;

    void api.login(email, password)
      .then(() => afterSignIn())
      .catch((err: unknown) => {
        if (err instanceof ApiError) {
          setFieldError('login-email-error', err.fieldError('email') ?? null);
          setFieldError('login-password-error', err.fieldError('password') ?? null);
          if (err.fields.length === 0) { notice.textContent = err.message; notice.classList.add('bad'); }
        } else {
          notice.textContent = 'Could not reach the server. Check your connection.';
          notice.classList.add('bad');
        }
      })
      .finally(() => { if (button !== null) button.disabled = false; });
  });

  registerForm.addEventListener('submit', (e) => {
    e.preventDefault();
    const displayName = el<HTMLInputElement>('reg-name').value;
    const email = el<HTMLInputElement>('reg-email').value;
    const password = el<HTMLInputElement>('reg-password').value;
    const notice = el('register-notice');
    notice.textContent = '';
    notice.classList.remove('bad');

    const nameProblem = validateDisplayName(displayName);
    const emailProblem = validateEmail(email);
    const passwordProblem = validatePassword(password);
    setFieldError('reg-name-error', nameProblem);
    setFieldError('reg-email-error', emailProblem);
    setFieldError('reg-password-error', passwordProblem);
    if (nameProblem !== null || emailProblem !== null || passwordProblem !== null) return;

    const button = registerForm.querySelector<HTMLButtonElement>('button.primary');
    if (button !== null) button.disabled = true;

    void api.register(email, password, displayName)
      .then((result) => {
        notice.textContent = 'Account created. Check your email to confirm the address, then sign in.';
        if (result.devVerificationUrl !== undefined) {
          notice.textContent += ' (Development: the link is in the server log.)';
        }
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError) {
          setFieldError('reg-name-error', err.fieldError('displayName') ?? null);
          setFieldError('reg-email-error', err.fieldError('email') ?? null);
          setFieldError('reg-password-error', err.fieldError('password') ?? null);
          if (err.fields.length === 0) { notice.textContent = err.message; notice.classList.add('bad'); }
        } else {
          notice.textContent = 'Could not reach the server. Check your connection.';
          notice.classList.add('bad');
        }
      })
      .finally(() => { if (button !== null) button.disabled = false; });
  });

  el('forgot').addEventListener('click', () => {
    const email = el<HTMLInputElement>('login-email').value;
    const notice = el('login-notice');
    const problem = validateEmail(email);
    if (problem !== null) { setFieldError('login-email-error', problem); return; }

    void api.forgotPassword(email)
      .then((result) => { notice.textContent = result.message; notice.classList.remove('bad'); })
      .catch(() => {
        notice.textContent = 'Could not reach the server. Check your connection.';
        notice.classList.add('bad');
      });
  });
}

// --- boot ------------------------------------------------------------------

async function boot(): Promise<void> {
  applyAccessibility(settings);
  wireAuth();
  wireSettings();

  if (import.meta.env.DEV) {
    const problems = validateZones();
    if (problems.length > 0) {
      console.error('Zone data problems:\n' + problems.map((p) => '  - ' + p).join('\n'));
    }
  }

  progress(10, 'Checking your session…');
  const user = await api.restore().catch(() => null);

  if (user !== null && isSignedIn()) {
    await afterSignIn();
  } else {
    hide(dom.loading);
    show(dom.auth);
  }

  if ('serviceWorker' in navigator && import.meta.env.PROD) {
    navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  }
}

void boot().catch((err: unknown) => {
  console.error(err);
  dom.loadingDetail.textContent =
    'Something went wrong starting the game. Please reload the page.';
});
